<template>
	<div class="h-full flex justify-center">
		<div class="flex flex-col h-full w-full max-w-xl">
			<div v-if="!loading" class="grow px-3 sm:px-5 my-3 2xl:my-6">
				<!-- Month rate -->
				<div class="h-full flex flex-col justify-center">
					<panel :title="$t('eny.application_form')" :subtitle="$t('eny.fillout_msg')">
						<!-- Amount -->
						<div class="text-base font-bold pt-4">{{$t('eny.desired_amount_in_CHF')}}</div>
						<div class="flex flex-col sm:flex-row sm:gap-6 pt-1">
							<div class="grow flex items-center">
								<ejs-slider id='amount' v-model:value="amount_loc" type="MinRange" min="1000" max="250000" step="1000"></ejs-slider>
							</div>
							<div class="flex flex-col items-center">
								<div class="bg-color-2 text-white font-bold text-lg rounded-full flex items-center">
									<div class="flex flex-row px-4 py-1 w-40 justify-between">
										<div>{{$t('eny.chf')}}</div>
										<div>{{amount_view}}</div>
									</div>
								</div>
							</div>
						</div>
						<!-- Duration -->
						<div class="text-base font-bold pt-4">{{duration_text}}</div>
						<div class="flex flex-col sm:flex-row sm:gap-6 pt-1">
							<div :class="{grow: true, flex: true, 'items-center': true, 'e-disabled': !durationEnabled}">
								<ejs-slider id='duration' v-model:value="duration_loc" type="MinRange" min="1" :max="max_duration" step="1" :limits='limits' :enabled="durationEnabled"></ejs-slider>
							</div>
							<div class="flex flex-col items-center">
								<div class="bg-color-2 text-white font-bold text-lg rounded-full flex items-center">
									<div class="flex flex-row px-4 py-1 w-40 justify-between">
										<div>{{duration_loc}}</div>
										<div>{{$t('eny.months')}}</div>
									</div>
								</div>
							</div>
						</div>
						<!-- Display of rating Info -->
						<rate :amount="amount_loc" :duration="duration_loc" :incl_insurance="incl_insurance_loc">
							<!-- Include insurance -->
							<div class="pt-4 flex flex-col items-center">
								<ejs-checkbox id="incl_insurance" name="incl_insurance" :label="$t('eny.incl_insurance')" v-model:checked="incl_insurance_loc" :value="incl_insurance_loc ? 1 : 0"></ejs-checkbox>
							</div>
							<!-- Extra Info -->
							<div class="text-sm pt-4 text-left">{{$t('eny.general_info_interest', {min_interest: min_interest, max_interest: max_interest})}}</div>
						</rate>
					</panel>
					<!-- Buttons -->
					<div v-if="allow_navigation" class="w-full px-10 md:px-20 py-3 2xl:pt-6 flex flex-row justify-center">
						<button type="button" class="eny-btn-blue px-8 py-2 text-2xl font-bold" @click="nextStep">{{$t('eny.forward')}}</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import { TextBoxComponent, SliderComponent } from '@syncfusion/ej2-vue-inputs';
import { CheckBoxComponent } from "@syncfusion/ej2-vue-buttons";
import Panel from "../Base/Panel.vue";
import Rate from "./Offer/Rate.vue";
import {mapActions, mapGetters, mapMutations} from 'vuex';

//prevent customer from changing the credit duration for the firm with firmId
const fixedDuration = { firmId: 154, duration: 84 }

export default {
	name: "Offer",
	components: {
		'ejs-textbox': TextBoxComponent,
		'ejs-slider' : SliderComponent,
		'ejs-checkbox': CheckBoxComponent,
		'panel': Panel,
		'rate' : Rate,
	},
	data() {
		return {
			// controls' settings
			limits: { enabled: true, minStart: 6 },
			tooltip: { placement: 'Before', isVisible: true, showOn: 'Focus' },
			max_duration: 120
		};
	},
	created() {
		this.fetchConfig();
		// If the page was called with firm name in the URL or with enyFinance as firm,
		// load the firm if the firm exists or redirect to site root.
		if (this.$route.params.firm || this.$route.name==='offer_eny') {
			const firm = (this.$route.params.firm) ? this.$route.params.firm : this.$route.meta.firmName
			this.$store.dispatch('user/loadFirm', firm)
			.then(() => {
				this.UPDATE_USER_FIRM(this.firm.id);
			}) // firm not found, redirect user away
			.catch(() => {
				this.$router.push('/');
			});
			if (!this.request.id) {
				this.$store.dispatch('customer/store_request_base_confirmed', false)
			}
		}
	},
	methods: {
		nextStep: function() {
			this.$router.push('/offer/checklist');
		},
		...mapMutations('customer', ['UPDATE_AMOUNT', 'UPDATE_DURATION', 'UPDATE_INCL_INSURANCE', 'UPDATE_USER_FIRM']),
		...mapActions('config', ['fetchConfig']),
	},
	computed: {
		...mapGetters('customer', ['request', 'amount_view']),
		...mapGetters('customer', {customerUser: 'user'}),
		...mapGetters('user', ['loading', 'firm']),
		...mapGetters('config', ['config']),
		min_interest() {
			return this.config.min_interest;
		},
		max_interest() {
			return this.config.max_interest;
		},
		amount_loc: {
			get() {
				return this.request.credit_amount;
			},
			set(value) {
				this.UPDATE_AMOUNT(value);
				if (value > 80000) {
					this.max_duration = 84
					if (this.duration_loc > this.max_duration) {
						this.duration_loc = this.max_duration
					}
				}
				else if (this.max_duration != 120) {
					this.max_duration = 120
				}
			},
		},
		duration_loc: {
			get() {
				return this.durationEnabled ? this.request.credit_duration : fixedDuration.duration;
			},
			set(value) {
				this.UPDATE_DURATION(value);
			},
		},
		incl_insurance_loc: {
			get() {
				return this.request.incl_insurance;
			},
			set(value) {
				this.UPDATE_INCL_INSURANCE(value);
			},
		},
		allow_navigation: function() {
			return this.firm.id || this.customerUser.id || (this.$keycloak && this.$keycloak.authenticated);
		},
		durationEnabled: function() {
			return !this.firm.id || this.firm.id !== fixedDuration.firmId;
		},
		duration_text: function() {
			return this.durationEnabled ? this.$t('eny.term_6_?_months', {max_months: this.max_duration}) :  this.$t('eny.term_months', {months: fixedDuration.duration});
		},
	}
}
</script>
